import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { AudioMutedOutlined } from '@ant-design/icons';
import type { NetworkQuality } from '@zoom/videosdk';
import { IconFont } from '../../../component/icon-font';
import classNames from 'classnames';
import './avatar.scss';
import { Participant } from '../../../index-types';
import { useHover } from '../../../hooks';
import AvatarMore from './avatar-more';
interface AvatarProps {
  participant: Participant;
  style?: { [key: string]: string };
  isActive: boolean;
  className?: string;
  networkQuality?: NetworkQuality;
}
const MAX_VALUE_QUALITY = 5;
const networkQualityIcons = ['bad', 'bad', 'normal', 'good', 'good', 'good'];
const Avatar = (props: AvatarProps) => {
  const { participant, style, isActive, className, networkQuality } = props;
  const { displayName, audio, muted, bVideoOn, userId, isInFailover } = participant;
  const [fontSize, setFontSize] = useState(50);
  const [cornerFontSize, cornerSetFontSize] = useState(14);
  const containerRef = useRef(null);
  const avatarRef = useRef(null);
  const isHover = useHover(containerRef);
  const [networkQualityValue, setNetworkQualityValue] = useState(5);

  useEffect(() => {
    if (networkQuality !== undefined) {
      setNetworkQualityValue(
        Math.min(networkQuality?.downlink ?? MAX_VALUE_QUALITY, networkQuality?.uplink ?? MAX_VALUE_QUALITY)
      );
    }
  }, [networkQuality]);
  useLayoutEffect(() => {
    if (avatarRef.current) {
      const { width } = (avatarRef.current as HTMLDivElement).getBoundingClientRect();

      setFontSize(Math.max(14, Math.ceil(width / 3)));
      cornerSetFontSize(width >= 160 ? 14 : 12);
    }
  }, [displayName]);

  const getInitials = () => {
    const parts = displayName.trim().split(' ');

    if (parts.length === 1) return parts[0][0].toUpperCase();

    const first = parts[0];
    const last = parts[parts.length - 1];

    return (first[0] + last[0]).toUpperCase();
  };

  const stringToColor = (str?: string) => {
    const safeStr = str?.trim() || 'User';
    var hash = 0;
    for (var i = 0; i < safeStr.length; i++) {
      hash = safeStr.charCodeAt(i) + ((hash << 5) - hash);
    }
    var colour = '#';
    for (var i = 0; i < 3; i++) {
      var value = (hash >> (i * 8)) & 0xff;
      colour += ('00' + value.toString(16)).substr(-2);
    }
    return colour;
  };

  return (
    <div
      ref={containerRef}
      className={classNames('avatar', { 'avatar-active': isActive }, className)}
      style={{ ...style, background: bVideoOn ? 'transparent' : 'linear-gradient(135deg, #1a2a3a, #000)' }}
    >
      <div className="corner-name" style={{ fontSize: cornerFontSize }}>
        {audio === 'computer' && muted && <AudioMutedOutlined style={{ color: '#f00' }} />}
        <IconFont type={`icon-network-${networkQualityValue}`} />
        {isInFailover && (
          <IconFont type="icon-reconnect" style={{ color: '#FF9209', animation: 'loading 3s linear infinite' }} />
        )}
        <span
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {displayName}
        </span>
      </div>
      {!bVideoOn && (
        <div ref={avatarRef} className="center-name" style={{ backgroundColor: stringToColor(displayName), fontSize }}>
          {getInitials()}
        </div>
      )}
      {!isInFailover && <AvatarMore userId={userId} isHover={isHover} />}
    </div>
  );
};

export default Avatar;
